import { Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

export const CodeDisplayer = ({ mfaLink }: { mfaLink: string }): JSX.Element => {
  const [copied, setCopied] = useState(false);

  const extractSecretFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('secret') ?? '';
    } catch {
      const match = url.match(/[?&]secret=([^&]+)/);
      return match ? match[1] : '';
    }
  };

  const manualSecret = extractSecretFromUrl(mfaLink);

  const handleCopy = () => {
    navigator.clipboard.writeText(manualSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-col gap-3.5 w-full">
      {/* Step 1 - QR Code */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-[var(--ink)]">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-blue">
            1
          </span>
          Step 1: Scan QR Code
        </h2>
        <div className="mb-2 flex justify-center">
          <div className="rounded-xl border border-[var(--line)] bg-surface p-3">
            <div className="h-[180px] w-[180px]">
              <QRCodeSVG value={mfaLink} width="100%" height="100%" />
            </div>
          </div>
        </div>
        <p className="text-center text-sm text-[var(--ink-2)]">Open Google Authenticator and scan this code</p>
      </div>

      {/* Step 2 - Manual Entry */}
      <div>
        <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-[var(--ink)]">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-blue">
            2
          </span>
          Step 2: Or enter manually
        </h2>
        <div className="mb-1 flex w-full flex-col gap-2 sm:flex-row">
          <Input readOnly value={manualSecret} className="w-full bg-[var(--sunken)] font-mono" />
          <Button
            type="button"
            onClick={handleCopy}
            variant="outline"
            className="flex h-9 w-full items-center justify-center gap-1 px-3 text-sm sm:w-auto"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-xs text-[var(--ink-3)]">
          Enter this key in Google Authenticator if you can&apos;t scan the QR code
        </p>
      </div>
    </div>
  );
};
